// codifica.ts — trasforma i fotogrammi già disegnati (e l'audio già montato) in un file video.
//
// Strada principale: WebCodecs (VideoEncoder H.264, o VP9 se manca, + AudioEncoder AAC o Opus) e il muxer
// `mp4-muxer` → MP4 con durata e indici giusti, più veloce del tempo reale, a tempi esatti.
// Ripiego: canvas.captureStream + MediaRecorder, in tempo reale (Firefox senza H.264, browser vecchi, oppure
// audio richiesto ma AudioEncoder assente). Nel ripiego il file è quello che dà il browser (MP4 su Safari,
// spesso WebM su Chrome/Firefox).
// Il muxer si carica solo quando serve (prima clip): non pesa sull'avvio dell'app. Se non si carica (rete, versione
// dell'app senza il pacchetto) si usa il ripiego MediaRecorder.
let moduloMuxer: Promise<typeof import("mp4-muxer") | null> | null = null;
export function caricaMuxer(): Promise<typeof import("mp4-muxer") | null> {
  moduloMuxer ??= import("mp4-muxer").catch(() => {
    moduloMuxer = null;
    return null;
  });
  return moduloMuxer;
}
import { ErroreClip } from "./errori.ts";

export interface Disegnatore {
  /** Disegna sulla tela il fotogramma `i` (0..totale-1). */
  disegna(i: number): Promise<void>;
  tela: HTMLCanvasElement;
  totale: number;
  fps: number;
}

export interface Risultato {
  blob: Blob;
  tipo: string;
}

const CODEC_VIDEO: { codec: string; muxer: "avc" | "vp9" }[] = [
  { codec: "avc1.640028", muxer: "avc" }, // High 4.0
  { codec: "avc1.4d0028", muxer: "avc" }, // Main 4.0
  { codec: "avc1.42e028", muxer: "avc" }, // Baseline 4.0
  { codec: "avc1.42001f", muxer: "avc" }, // Baseline 3.1 (720×1280 a 30 fps ci sta al limite)
  { codec: "vp09.00.31.08", muxer: "vp9" },
];

export interface SceltaVideo {
  config: VideoEncoderConfig;
  muxer: "avc" | "vp9";
}

export async function scegliVideo(larghezza: number, altezza: number, fps: number): Promise<SceltaVideo | null> {
  if (typeof VideoEncoder === "undefined" || typeof VideoFrame === "undefined") return null;
  const bitrate = Math.round(larghezza * altezza * fps * 0.11); // ~3 Mbit/s a 720×1280×30
  for (const c of CODEC_VIDEO) {
    const config: VideoEncoderConfig = {
      codec: c.codec,
      width: larghezza,
      height: altezza,
      bitrate,
      framerate: fps,
      latencyMode: "quality",
      ...(c.muxer === "avc" ? { avc: { format: "avc" as const } } : {}),
    };
    try {
      const r = await VideoEncoder.isConfigSupported(config);
      if (r.supported) return { config: r.config ?? config, muxer: c.muxer };
    } catch {
      // config non valida per questo browser: si prova la successiva
    }
  }
  return null;
}

export interface SceltaAudio {
  config: AudioEncoderConfig;
  muxer: "aac" | "opus";
}

export async function scegliAudio(frequenza: number): Promise<SceltaAudio | null> {
  if (typeof AudioEncoder === "undefined" || typeof AudioData === "undefined") return null;
  for (const [codec, muxer] of [["mp4a.40.2", "aac"], ["opus", "opus"]] as const) {
    const config: AudioEncoderConfig = { codec, sampleRate: frequenza, numberOfChannels: 1, bitrate: 96_000 };
    try {
      const r = await AudioEncoder.isConfigSupported(config);
      if (r.supported) return { config: r.config ?? config, muxer };
    } catch {
      // si prova il successivo
    }
  }
  return null;
}

const attendi = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function controllaAnnullo(segnale?: AbortSignal): void {
  if (segnale?.aborted) throw new ErroreClip("annullata", "Montaggio annullato.");
}

/** Strada WebCodecs + mp4-muxer. */
export async function codificaWebCodecs(
  d: Disegnatore,
  video: SceltaVideo,
  audio: { scelta: SceltaAudio; campioni: Float32Array; frequenza: number } | null,
  avanzamento: (p: number) => void,
  segnale?: AbortSignal,
): Promise<Risultato> {
  const mm = await caricaMuxer();
  if (!mm) throw new ErroreClip("non_supportato", "Il muxer MP4 non si è caricato.");
  const target = new mm.ArrayBufferTarget();
  const muxer = new mm.Muxer({
    target,
    video: { codec: video.muxer, width: d.tela.width, height: d.tela.height, frameRate: d.fps },
    ...(audio ? { audio: { codec: audio.scelta.muxer, numberOfChannels: 1, sampleRate: audio.frequenza } } : {}),
    fastStart: "in-memory",
    firstTimestampBehavior: "offset",
  });
  let errore: unknown = null;
  const venc = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => {
      errore = e;
    },
  });
  venc.configure(video.config);
  let aenc: AudioEncoder | null = null;
  try {
    const durata = 1e6 / d.fps;
    for (let i = 0; i < d.totale; i++) {
      controllaAnnullo(segnale);
      if (errore) throw errore;
      await d.disegna(i);
      const fotogramma = new VideoFrame(d.tela, { timestamp: Math.round(i * durata), duration: Math.round(durata) });
      venc.encode(fotogramma, { keyFrame: i % (d.fps * 2) === 0 });
      fotogramma.close();
      // Contropressione: non accumulare fotogrammi in coda (memoria).
      while (venc.encodeQueueSize > 6) await attendi(4);
      avanzamento((i + 1) / d.totale * (audio ? 0.92 : 0.98));
    }
    await venc.flush();
    if (errore) throw errore;
    if (audio) {
      aenc = new AudioEncoder({
        output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
        error: (e) => {
          errore = e;
        },
      });
      aenc.configure(audio.scelta.config);
      // L'audio non deve allungare il file oltre il video: si taglia a un multiplo esatto del pacchetto del codec
      // (Opus 20 ms = 960 campioni a 48 kHz, AAC 1024 campioni) che non superi la durata del video.
      const pacchetto = audio.scelta.muxer === "opus" ? Math.round(audio.frequenza / 50) : 1024;
      // Un pacchetto in meno: l'encoder ne aggiunge uno alla fine per il ritardo iniziale (pre-skip/priming).
      const massimo = (Math.floor(((d.totale / d.fps) * audio.frequenza) / pacchetto) - 1) * pacchetto;
      const campioni = audio.campioni.subarray(0, Math.min(audio.campioni.length, massimo));
      const blocco = pacchetto;
      for (let i = 0; i + blocco <= campioni.length; i += blocco) {
        controllaAnnullo(segnale);
        const pezzo = campioni.slice(i, i + blocco);
        const ad = new AudioData({
          format: "f32-planar",
          sampleRate: audio.frequenza,
          numberOfFrames: pezzo.length,
          numberOfChannels: 1,
          timestamp: Math.round((i / audio.frequenza) * 1e6),
          data: pezzo,
        });
        aenc.encode(ad);
        ad.close();
        while (aenc.encodeQueueSize > 16) await attendi(2);
      }
      await aenc.flush();
      if (errore) throw errore;
    }
    muxer.finalize();
    avanzamento(1);
    const codec = audio ? `${video.config.codec},${audio.scelta.config.codec}` : video.config.codec;
    return { blob: new Blob([target.buffer], { type: "video/mp4" }), tipo: `video/mp4;codecs=${codec}` };
  } finally {
    if (venc.state !== "closed") venc.close();
    if (aenc && aenc.state !== "closed") aenc.close();
  }
}

const TIPI_REGISTRATORE = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

export function tipoRegistratore(): string | null {
  if (typeof MediaRecorder === "undefined" || typeof HTMLCanvasElement === "undefined" || !("captureStream" in HTMLCanvasElement.prototype)) return null;
  for (const t of TIPI_REGISTRATORE) if (MediaRecorder.isTypeSupported(t)) return t;
  return null;
}

/** Ripiego in tempo reale: canvas.captureStream + MediaRecorder (con l'audio suonato da WebAudio). */
export async function codificaRegistratore(
  d: Disegnatore,
  audio: { campioni: Float32Array; frequenza: number } | null,
  avanzamento: (p: number) => void,
  segnale?: AbortSignal,
): Promise<Risultato> {
  const tipo = tipoRegistratore();
  if (!tipo) throw new ErroreClip("non_supportato", "Questo browser non sa registrare video da una tela.");
  const flusso = d.tela.captureStream(d.fps);
  const traccia = flusso.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
  let ctx: AudioContext | null = null;
  let sorgente: AudioBufferSourceNode | null = null;
  if (audio && typeof AudioContext !== "undefined") {
    try {
      ctx = new AudioContext({ sampleRate: audio.frequenza });
      await ctx.resume();
      if (ctx.state === "running") {
        const buf = ctx.createBuffer(1, Math.max(1, audio.campioni.length), audio.frequenza);
        buf.copyToChannel(new Float32Array(audio.campioni), 0);
        sorgente = ctx.createBufferSource();
        sorgente.buffer = buf;
        const dest = ctx.createMediaStreamDestination();
        sorgente.connect(dest);
        for (const t of dest.stream.getAudioTracks()) flusso.addTrack(t);
      }
    } catch {
      sorgente = null; // clip muta piuttosto che niente clip
    }
  }
  const pezzi: Blob[] = [];
  const rec = new MediaRecorder(flusso, { mimeType: tipo, videoBitsPerSecond: Math.round(d.tela.width * d.tela.height * d.fps * 0.11) });
  rec.ondataavailable = (e) => {
    if (e.data.size) pezzi.push(e.data);
  };
  const finito = new Promise<void>((ok) => {
    rec.onstop = () => ok();
  });
  try {
    await d.disegna(0);
    rec.start(1000);
    sorgente?.start();
    const t0 = performance.now();
    for (let i = 0; i < d.totale; i++) {
      controllaAnnullo(segnale);
      await d.disegna(i);
      traccia?.requestFrame?.();
      avanzamento((i + 1) / d.totale);
      const prossimo = t0 + ((i + 1) * 1000) / d.fps;
      const resta = prossimo - performance.now();
      if (resta > 0) await attendi(resta);
    }
    rec.stop();
    await finito;
  } catch (e) {
    if (rec.state !== "inactive") rec.stop();
    throw e;
  } finally {
    sorgente?.disconnect();
    for (const t of flusso.getTracks()) t.stop(); // sono tracce nostre (tela e WebAudio), non dell'app
    if (ctx) await ctx.close().catch(() => undefined);
  }
  const base = tipo.split(";")[0] ?? tipo;
  return { blob: new Blob(pezzi, { type: base }), tipo };
}
